// Restaurant registration form (Task 13.1).
//
// Inline validation mirrors the backend rules by delegating to the shared
// domain validators (validateRestaurantRegistration) so the client and server
// never disagree (Req 3.1, 3.3). On rejection the entered values are retained
// and every violated field is flagged. On success the verification document is
// uploaded and the registerRestaurant callable is invoked; the backend remains
// authoritative.

import { useState } from "react";
import {
  FOOD_CATEGORIES,
  validateRestaurantRegistration,
  type FoodCategory,
  type RestaurantRegistrationField,
} from "@sharebite/domain";
import { Card } from "../../components/primitives";
import * as api from "../../lib/api";
import { formatBytes, toDocumentMeta } from "../../lib/fileMeta";

const FIELD_LABELS: Record<RestaurantRegistrationField, string> = {
  restaurantName: "Restaurant Name (1-100 characters) is required.",
  ownerName: "Owner Name (1-100 characters) is required.",
  phoneNumber: "Phone Number must be 10-15 digits.",
  address: "Address (1-250 characters) is required.",
  foodCategoriesServed: "Select 1 to 3 food categories.",
  document: "Upload a PDF, JPEG, or PNG document up to 10 MB.",
  duplicateProfile: "A restaurant profile already exists for this account.",
};

export function RestaurantRegistrationForm({
  uid,
  onSubmitted,
}: {
  uid: string;
  onSubmitted: () => void;
}): JSX.Element {
  const [restaurantName, setRestaurantName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [categories, setCategories] = useState<FoodCategory[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [violations, setViolations] = useState<RestaurantRegistrationField[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function toggleCategory(c: FoodCategory): void {
    setCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  }

  const has = (f: RestaurantRegistrationField) => violations.includes(f);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitError(null);
    const payload = {
      restaurantName,
      ownerName,
      phoneNumber,
      address,
      foodCategoriesServed: categories,
      document: file
        ? toDocumentMeta(file)
        : { format: "none", sizeBytes: 0 },
    };
    const result = validateRestaurantRegistration(payload, false);
    if (!result.accepted) {
      setViolations(result.violations);
      return;
    }
    setViolations([]);
    setSubmitting(true);
    try {
      const verificationDocRef = await api.uploadVerificationDocument(
        uid,
        file as File,
      );
      await api.registerRestaurant({
        ...payload,
        foodCategoriesServed: categories,
        verificationDocRef,
      });
      onSubmitted();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Submission failed. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="sb-main">
      <Card title="Restaurant registration">
        <form onSubmit={handleSubmit} noValidate>
          <div className="sb-field">
            <label htmlFor="r-name">Restaurant Name</label>
            <input
              id="r-name"
              className="sb-input"
              value={restaurantName}
              onChange={(e) => setRestaurantName(e.target.value)}
            />
            {has("restaurantName") && (
              <span className="sb-field__error">{FIELD_LABELS.restaurantName}</span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="r-owner">Owner Name</label>
            <input
              id="r-owner"
              className="sb-input"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
            />
            {has("ownerName") && (
              <span className="sb-field__error">{FIELD_LABELS.ownerName}</span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="r-phone">Phone Number</label>
            <input
              id="r-phone"
              className="sb-input"
              inputMode="numeric"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            {has("phoneNumber") && (
              <span className="sb-field__error">{FIELD_LABELS.phoneNumber}</span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="r-address">Address</label>
            <textarea
              id="r-address"
              className="sb-textarea"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            {has("address") && (
              <span className="sb-field__error">{FIELD_LABELS.address}</span>
            )}
          </div>

          <div className="sb-field">
            <label>Food Categories Served (1-3)</label>
            <div className="sb-row">
              {FOOD_CATEGORIES.map((c) => (
                <label key={c} className="sb-pill" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={categories.includes(c)}
                    onChange={() => toggleCategory(c)}
                  />{" "}
                  {c}
                </label>
              ))}
            </div>
            {has("foodCategoriesServed") && (
              <span className="sb-field__error">
                {FIELD_LABELS.foodCategoriesServed}
              </span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="r-doc">
              Business Registration / FSSAI document (PDF, JPEG, PNG, &le; 10 MB)
            </label>
            <input
              id="r-doc"
              type="file"
              className="sb-input"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file && (
              <span className="sb-muted">
                {file.name} ({formatBytes(file.size)})
              </span>
            )}
            {has("document") && (
              <span className="sb-field__error">{FIELD_LABELS.document}</span>
            )}
          </div>

          {has("duplicateProfile") && (
            <p className="sb-field__error">{FIELD_LABELS.duplicateProfile}</p>
          )}
          {submitError && <p className="sb-field__error">{submitError}</p>}

          <button type="submit" className="sb-btn" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit application"}
          </button>
        </form>
      </Card>
    </div>
  );
}
